import { useCallback, useEffect, useRef, useState } from 'react'
import API from '../../../services/API'

export function useTreatmentLocationSnapshot(locationID, intervalMs = 2000) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [syncState, setSyncState] = useState('connecting')
  const [lastUpdated, setLastUpdated] = useState(null)

  const versionRef = useRef(null)
  const failedPollsRef = useRef(0)

  const fetchSnapshot = useCallback(async () => {
    if (!locationID) return
    try {
      const res = await API.get(
        `/commandcontrol/treatment-locations/${locationID}/snapshot`,
        {
          headers: versionRef.current
            ? {
                'If-None-Match': `"${versionRef.current}"`,
              }
            : {},
          validateStatus: (status) => status === 200 || status === 304,
        },
      )
      failedPollsRef.current = 0
      setLastUpdated(new Date())
      setSyncState('live')
      if (res.status === 304) {
        return
      }
      const body = res.data
      versionRef.current = body?.version
      setData(body)
    } catch (err) {
      failedPollsRef.current += 1

      if (!navigator.onLine || failedPollsRef.current >= 3) {
        setSyncState('offline')
      } else {
        setSyncState('delayed')
      }

      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [locationID])

  useEffect(() => {
    if (!locationID) return

    setLoading(true)
    setSyncState('connecting')
    setLastUpdated(null)
    failedPollsRef.current = 0
    versionRef.current = null

    fetchSnapshot()

    const interval = setInterval(fetchSnapshot, intervalMs)

    return () => clearInterval(interval)
  }, [locationID, intervalMs, fetchSnapshot])

  useEffect(() => {
    if (!lastUpdated || syncState === 'offline') return

    const timer = setInterval(() => {
      const secondsSinceSync =
        (Date.now() - new Date(lastUpdated).getTime()) / 1000

      if (secondsSinceSync > 10) {
        setSyncState('delayed')
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [lastUpdated, syncState])

  useEffect(() => {
    const online = () => {
      setSyncState('connecting')
      fetchSnapshot()
    }

    const offline = () => setSyncState('offline')

    window.addEventListener('online', online)
    window.addEventListener('offline', offline)

    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
    }
  }, [fetchSnapshot])

  return {
    data,
    loading,
    syncState,
    lastUpdated,
    refresh: fetchSnapshot,
  }
}
